import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { showErrorMessage, showSuccessMessage } from 'helpers/notifications';
import { FreightsService } from 'service/FreightsService';
import { ENTITY } from 'constants/entity';
import { useMedia } from 'hooks/useMedia';

import ExchangeFreightInfo from './ExchangeFreightInfo';
import Loading from 'components/Loading';
import TooltipButton from 'components/common/InfoPopup/TooltipButton';
import { ICON } from 'components/common/IconButton';
import DeleteModal from 'components/PopupForms/DeleteDialog/DeleteModal';
import ContentContainer from 'components/common/InfoPopup/ContentContainer';
import { useChatSidebar } from 'context/ChatSidebarContext';
import styles from './styles.module.scss';

export default function ExchangeFreightPopup({ onClose, data, updateData, mode, isOpen, searchCoordinates }) {
  const navigate = useNavigate();
  const profileData = useSelector((state) => state.profile.profileData);
  const { t } = useTranslation();
  const { openChat, openPopupChat } = useChatSidebar();
  const { isDesktop } = useMedia();

  // Определяем владельца раньше, чтобы использовать в handleOpenChat
  const isMyExchange = data && profileData && profileData.id === data.user_id;

  const handleOpenChat = () => {
    // Владелец пишет клиенту (dealEmployee), клиент пишет владельцу (user)
    const employee = isMyExchange ? data.dealEmployee : data.user;
    const employeeId = employee?.id || data.user_id;
    // Передаём данные о грузе для создания запроса на сделку
    const dealData = {
      item: data,
      type: ENTITY.CARGO,
      employee: {
        id: employeeId,
        firstname: employee?.firstname || data.firstname,
        lastname: employee?.lastname || data.lastname,
        picture: employee?.picture || data.picture,
        company: employee?.company || { name: data.name },
        organization_name: employee?.organization_name || data.name,
      },
    };

    if (isDesktop) {
      openChat(employeeId, dealData);
    } else {
      // На мобильных открываем попап-чат
      openPopupChat({
        employee: dealData.employee,
        item: dealData.item,
        type: dealData.type,
      });
    }
  };

  const handleEditClick = () => {
    navigate(`/freight/edit?id=${data.id}`);
  };

  const handleDeleteClick = (id, callback) => {
    FreightsService.deleteFromMyCargo(id)
      .then((res) => {
        showSuccessMessage(t('successDelete'));
        onClose();
        callback();
        updateData();
      })
      .catch((e) => {
        showErrorMessage(t('error'));
      });
  };

  const handleUpToTopClick = () => {
    FreightsService.exchangeUp(data.id)
      .then((res) => {
        showSuccessMessage(t('success'));
        updateData();
      })
      .catch((e) => {
        console.log(e);
        showErrorMessage(t('error'));
      });
  };

  const handleToggleActive = () => {
    const wasActive = data.is_active;
    FreightsService.toggleActivation(data.id)
      .then((res) => {
        showSuccessMessage(t('success'));
        if (wasActive) {
          onClose();
        }
        updateData();
      })
      .catch((e) => {
        console.log(e);
        showErrorMessage(t('error'));
      });
  };

  // Кнопка чата: для создателя - только когда есть dealEmployee (сделка принята), для других - всегда
  const showChatButton = data && data.use_chat && (
    isMyExchange
      ? !!data.dealEmployee  // Создатель видит кнопку когда есть клиент (сделка принята)
      : mode !== 'auction' && data.company_id !== profileData.company_id  // Другие могут связаться
  );

  const popupButtons = isMyExchange && (
    <div className={styles.buttons_container}>
      {!data.isExpired && !data.isActiveProposal && (
        <div className={styles.buttons_container__tooltip_container}>
          <p>{t('upToTop')}</p>
          <TooltipButton
            onClick={handleUpToTopClick}
            hintText={data.can_up_to_top ? t('upToTop') : t('upToTopDisabled')}
            disabled={!data.can_up_to_top}
            icon={ICON.toTop}
          />
        </div>
      )}
      {!data.isActiveProposal && (
        <div className={styles.buttons_container__tooltip_container}>
          <p>{t('edit')}</p>
          <TooltipButton onClick={handleEditClick} hintText={t('edit')} icon={ICON.edit} />
        </div>
      )}
      {!data.isExpired && !data.isActiveProposal && (
        <div className={styles.buttons_container__tooltip_container}>
          <p>{data.isActive ? t('removeFromStock') : t('addToStock')}</p>
          <TooltipButton
            onClick={handleToggleActive}
            hintText={data.is_active ? t('removeFromStock') : t('addToStock')}
            icon={data.is_active ? ICON.eyeInvisible : ICON.eye}
          />
        </div>
      )}
      <div className={styles.buttons_container__tooltip_container}>
        <p>{t('cargo.delete')}</p>
        <DeleteModal
          onDeleteClick={handleDeleteClick}
          data={{
            title: t('cargo.delete'),
            id: data.id,
            name: t('cargo.cargo'),
          }}
        />
      </div>
    </div>
  );

  return (
    <ContentContainer
      isOpen={isOpen}
      onClosePopup={onClose}
      popupButtons={popupButtons}
    >
      {data && Object.keys(data).length ? (
        <ExchangeFreightInfo
          data={data}
          mode={mode}
          showChatButton={showChatButton}
          handleOpenChat={handleOpenChat}
          mobileButtons={popupButtons}
          searchCoordinates={searchCoordinates}
        />
      ) : (
        <Loading />
      )}
    </ContentContainer>
  );
}
